import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Grid,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Work,
  People,
  TrendingUp,
  Schedule,
  Add,
  ArrowUpward,
  PostAdd,
  ViewList,
  Phone
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';

interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  totalApplications: number;
  newApplications: number;
  conversionRate: number;
  avgTimeToHire: number;
}

export const SimpleDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { entity, isSuperAdmin, isLoading: contextLoading } = useEntity();

  // Redirect super admins to Entity Analytics page
  useEffect(() => {
    if (!contextLoading && isSuperAdmin) {
      navigate('/admin/entity-analytics', { replace: true });
    }
  }, [isSuperAdmin, contextLoading, navigate]);

  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    conversionRate: 0,
    avgTimeToHire: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getAuthUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const entityId = entity?.id || user.entityId;

      // Fetch total jobs
      const { count: totalJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      // Fetch active jobs
      const { count: activeJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'active')
        .is('deleted_at', null);

      // Fetch total applications
      const { count: totalApplicationsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      // Fetch new applications (last 7 days)
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const { count: newApplicationsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .gte('submitted_at', oneWeekAgo.toISOString())
        .is('deleted_at', null);

      // Fetch hired applications for conversion rate
      const { count: hiredCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'hired')
        .is('deleted_at', null);

      // Calculate conversion rate
      const conversionRate = totalApplicationsCount && totalApplicationsCount > 0
        ? Math.round((hiredCount || 0) / totalApplicationsCount * 100 * 10) / 10
        : 0;

      // Fetch applications with hire dates to calculate avg time to hire
      const { data: hiredApplications } = await supabase
        .from('applications')
        .select('submitted_at, reviewed_at')
        .eq('entity_id', entityId)
        .eq('status', 'hired')
        .not('reviewed_at', 'is', null)
        .is('deleted_at', null)
        .limit(50);

      let avgTimeToHire = 0;
      if (hiredApplications && hiredApplications.length > 0) {
        const totalDays = hiredApplications.reduce((sum, app) => {
          const submitted = new Date(app.submitted_at);
          const reviewed = new Date(app.reviewed_at);
          const days = Math.ceil((reviewed.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0);
        avgTimeToHire = Math.round(totalDays / hiredApplications.length);
      }

      setStats({
        totalJobs: totalJobsCount || 0,
        activeJobs: activeJobsCount || 0,
        totalApplications: totalApplicationsCount || 0,
        newApplications: newApplicationsCount || 0,
        conversionRate,
        avgTimeToHire
      });

    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  useEffect(() => {
    fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboardStats, entity?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 3, border: `2px solid ${theme.palette.error.main}` }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Dashboard
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={fetchDashboardStats}>
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Modern Header with Glassmorphism */}
      <Box sx={{
        position: 'relative',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        mb: 4,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
              letterSpacing: '-0.02em'
            }}>
              Dashboard
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Welcome back! Here's what's happening today.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            component={Link}
            to="/admin/jobs/post"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Post New Job
          </Button>
        </Box>
      </Box>

      {/* Stats Cards with Modern Design */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            borderRadius: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  width: 60,
                  height: 60,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.35)}`
                }}>
                  <Work sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    Total Jobs
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: theme.palette.primary.main }}>
                    {stats.totalJobs}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ArrowUpward sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                      {stats.activeJobs} active
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha('#10b981', 0.08)} 0%, ${alpha('#10b981', 0.03)} 100%)`,
            border: `1px solid ${alpha('#10b981', 0.1)}`,
            borderRadius: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${alpha('#10b981', 0.15)}`,
              border: `1px solid ${alpha('#10b981', 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: `radial-gradient(circle, ${alpha('#10b981', 0.1)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, #10b981 0%, #059669 100%)`,
                  width: 60,
                  height: 60,
                  boxShadow: `0 8px 20px ${alpha('#10b981', 0.35)}`
                }}>
                  <People sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    Applications
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#10b981' }}>
                    {stats.totalApplications}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ArrowUpward sx={{ fontSize: 16, color: '#10b981' }} />
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                      +{stats.newApplications} this week
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.light, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            borderRadius: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.secondary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  width: 60,
                  height: 60,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.35)}`
                }}>
                  <TrendingUp sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    Conversion Rate
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: theme.palette.secondary.main }}>
                    {stats.conversionRate}%
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ArrowUpward sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                      +2.3% this month
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha('#8B6BB1', 0.08)} 0%, ${alpha('#A590C5', 0.03)} 100%)`,
            border: `1px solid ${alpha('#8B6BB1', 0.1)}`,
            borderRadius: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${alpha('#8B6BB1', 0.15)}`,
              border: `1px solid ${alpha('#8B6BB1', 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: `radial-gradient(circle, ${alpha('#8B6BB1', 0.1)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, #8B6BB1 0%, #6B4490 100%)`,
                  width: 60,
                  height: 60,
                  boxShadow: `0 8px 20px ${alpha('#8B6BB1', 0.35)}`
                }}>
                  <Schedule sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    Avg. Days to Hire
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#8B6BB1' }}>
                    {stats.avgTimeToHire}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Industry avg: 24
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: 'text.primary' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              component={Link}
              to="/admin/jobs/post"
              sx={{
                p: 3,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`,
                }
              }}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                <PostAdd sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
                Post Job
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              component={Link}
              to="/admin/jobs"
              sx={{
                p: 3,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: `1px solid ${alpha('#10b981', 0.1)}`,
                background: `linear-gradient(135deg, ${alpha('#10b981', 0.03)} 0%, transparent 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha('#10b981', 0.15)}`,
                  border: `1px solid ${alpha('#10b981', 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha('#10b981', 0.08)} 0%, transparent 100%)`,
                }
              }}
            >
              <Avatar sx={{ bgcolor: '#10b981', width: 56, height: 56 }}>
                <ViewList sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
                Manage Jobs
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              component={Link}
              to="/admin/applications"
              sx={{
                p: 3,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.03)} 0%, transparent 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 100%)`,
                }
              }}
            >
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                <People sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
                Applications
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              component={Link}
              to="/admin/calls"
              sx={{
                p: 3,
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: `1px solid ${alpha('#8B6BB1', 0.1)}`,
                background: `linear-gradient(135deg, ${alpha('#8B6BB1', 0.03)} 0%, transparent 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha('#8B6BB1', 0.15)}`,
                  border: `1px solid ${alpha('#8B6BB1', 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha('#8B6BB1', 0.08)} 0%, transparent 100%)`,
                }
              }}
            >
              <Avatar sx={{ bgcolor: '#8B6BB1', width: 56, height: 56 }}>
                <Phone sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
                Call Screening
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Welcome Message - Modern Card */}
      <Card elevation={0} sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }
      }}>
        <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, mb: 1 }}>
                Welcome to your HR Dashboard! 🎉
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
                Your HR portal is successfully running! You can now manage jobs, view applications, and track analytics.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Box sx={{
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Typography variant="h6">💡</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              <strong>Live Data:</strong> All statistics are pulled from your database in real-time and filtered by your organization.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};